import { FORM_TYPE_MAP } from "constants/form-type-map";
import { FormType } from "enums/form";
import { useError, useHttpQueryService } from "hooks";
import { cn } from "lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeedbackService } from "services";
import FeedbackIcon from "components/common/ui/FeedbackIcon";
import { Button } from "components/ui/button";
import { useConsolidatedDrawer } from "./ConsolidatedDrawerContext";
import { ConsolidatedFeedbackSheetProps } from "./FeedbackConsolidatedSection";
import FeedbackSheet from "./FeedbackSheet";
import PulseSurveySheetContent from "./PulseSurveySheetContent";

const PulseSurveySheet = ({
  revieweeId,
  forms,
  showViewMore,
  isFetchingNextPage,
  onViewMore,
}: ConsolidatedFeedbackSheetProps) => {
  const {
    isOpen: open,
    selectedId: selectedFormId,
    shouldScrollToList: shouldScrollToAllFeedbacks,
    openDrawer,
    onOpenChange,
  } = useConsolidatedDrawer<number>();
  const formType = FormType.PULSE_SURVEY;
  const detailSectionRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToDetail, setShouldScrollToDetail] = useState(false);
  const selectedForm = selectedFormId
    ? forms.find((form) => form.id === selectedFormId)
    : undefined;

  const feedbacksRequest = useCallback(() => {
    return FeedbackService.getConsolidatedFeedbacks(revieweeId, {
      formType,
      take: forms.length,
      skip: 0,
    });
  }, [revieweeId, forms.length]);

  const {
    result: allPulseFeedbacks,
    isLoading,
    error: feedbacksError,
  } = useHttpQueryService({
    request: feedbacksRequest,
    requestOption: { enabled: open && !!selectedFormId },
    deps: [selectedFormId, revieweeId, forms.length],
  });

  useError(feedbacksError, true);

  useEffect(() => {
    if (!open) {
      setShouldScrollToDetail(false);
      return;
    }

    if (!shouldScrollToDetail || isLoading || !detailSectionRef.current) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      detailSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setShouldScrollToDetail(false);
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, shouldScrollToDetail, isLoading]);

  const feedbacks = useMemo(
    () =>
      allPulseFeedbacks?.filter(
        (feedback) => feedback.formId === selectedFormId,
      ),
    [allPulseFeedbacks, selectedFormId],
  );

  const list =
    forms.length > 0 ? (
      <ul className="list-none space-y-2 p-0">
        {forms.map((form) => {
          const isSelected = form.id === selectedFormId;

          return (
            <li key={form.id}>
              <Button
                type="button"
                variant="ghost"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setShouldScrollToDetail(true);
                  openDrawer(form.id);
                }}
                className={cn(
                  "h-auto w-full justify-start gap-2 rounded-lg px-4 py-3 duration-200 transition-all ease-in-out",
                  isSelected ? "bg-blue-15" : "bg-blue-10 hover:bg-blue-15",
                )}
              >
                <div className="shrink-0">
                  <FeedbackIcon type={formType} />
                </div>
                <span className="body-14-semibold truncate">{form.name}</span>
              </Button>
            </li>
          );
        })}
      </ul>
    ) : (
      <p className="body-14-regular text-neutral-50">
        There is no {FORM_TYPE_MAP[formType].toLowerCase()} cycle for this
        member.
      </p>
    );

  return (
    <FeedbackSheet
      open={open}
      formType={formType}
      onOpenChange={onOpenChange}
      detailTitle={selectedForm?.name ?? ""}
      isLoading={isLoading}
      feedbackTypeLabel={FORM_TYPE_MAP[formType]}
      listTitle="All Surveys"
      list={list}
      detail={
        <PulseSurveySheetContent
          feedbacks={feedbacks}
          emptyMessage="There is no pulse survey feedback for this cycle."
        />
      }
      detailSectionRef={detailSectionRef}
      shouldScrollToListSection={shouldScrollToAllFeedbacks}
      showViewMore={showViewMore}
      isFetchingNextPage={isFetchingNextPage}
      onViewMore={onViewMore}
    />
  );
};

export default PulseSurveySheet;
